import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { unwrapApiResponse, type Pagina } from '@shared/api';
import { environment } from 'src/environments/environment';
import type {
  CatalogoFiltros,
  CatalogoInput,
  CatalogoItem,
  CatalogoResumen,
  CategoriaOculta,
  FilasFueraDeCategoria,
  IntegridadCatalogoGeneral,
} from '@schemas/catalogos';
import type { CambioPrevisto, OperacionPrevisible } from '@schemas/cambios';

/**
 * Catálogos de lista simple contra `AdminPortal.API` (`/api/v1/catalogos`).
 *
 * Toda escritura lleva el motivo en `X-Motivo`, y antes de escribir la pantalla
 * pide el ENSAYO (`previsualizar`): el servidor ejecuta la operación, enseña la
 * fila antes y después, y la deshace.
 */
@Injectable({ providedIn: 'root' })
export class CatalogosService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/v1/catalogos`;

  /** Los catálogos con sus conteos. Uno cuya base no contesta llega con `error`. */
  listar(): Observable<readonly CatalogoResumen[]> {
    return this.http.get<unknown>(this.base).pipe(unwrapApiResponse<readonly CatalogoResumen[]>());
  }

  obtenerPagina(clave: string, filtros: CatalogoFiltros = {}): Observable<Pagina<CatalogoItem>> {
    return this.http
      .get<unknown>(`${this.base}/${encodeURIComponent(clave)}`, { params: aParams(filtros) })
      .pipe(unwrapApiResponse<Pagina<CatalogoItem>>());
  }

  previsualizar(
    clave: string,
    operacion: OperacionPrevisible,
    id?: number,
    datos?: CatalogoInput,
  ): Observable<readonly CambioPrevisto[]> {
    return this.http
      .post<unknown>(`${this.base}/${encodeURIComponent(clave)}/vista-previa`, {
        operacion,
        id,
        datos,
      })
      .pipe(unwrapApiResponse<readonly CambioPrevisto[]>());
  }

  // ── El catálogo general ────────────────────────────────────────────────

  /**
   * La revisión de integridad. El servidor la guarda media hora (recorre tablas
   * de un millón de filas); `refrescar` la rehace.
   */
  integridad(refrescar = false): Observable<IntegridadCatalogoGeneral> {
    const params = refrescar ? new HttpParams().set('refrescar', true) : undefined;
    return this.http
      .get<unknown>(`${this.base}/general/integridad`, { params })
      .pipe(unwrapApiResponse<IntegridadCatalogoGeneral>());
  }

  /** Las filas de esa columna que apuntan a ese valor (hasta mil). */
  filasFueraDeCategoria(columna: string, valor: number): Observable<FilasFueraDeCategoria> {
    const params = new HttpParams().set('columna', columna).set('valor', valor);
    return this.http
      .get<unknown>(`${this.base}/general/integridad/filas`, { params })
      .pipe(unwrapApiResponse<FilasFueraDeCategoria>());
  }

  /** Las categorías que el portal no muestra. */
  ocultas(): Observable<readonly CategoriaOculta[]> {
    return this.http
      .get<unknown>(`${this.base}/general/ocultas`)
      .pipe(unwrapApiResponse<readonly CategoriaOculta[]>());
  }

  /**
   * El motivo, percent-encoded: una cabecera HTTP no transporta acentos de forma
   * fiable, y un motivo en español casi siempre los lleva. El servidor lo
   * decodifica en un solo sitio (`MotivoActual`).
   */
  private conMotivo(motivo: string) {
    return { headers: { 'X-Motivo': encodeURIComponent(motivo) } };
  }

  crear(clave: string, input: CatalogoInput, motivo: string): Observable<CatalogoItem> {
    return this.http
      .post<unknown>(`${this.base}/${encodeURIComponent(clave)}`, input, this.conMotivo(motivo))
      .pipe(unwrapApiResponse<CatalogoItem>());
  }

  actualizar(
    clave: string,
    id: number,
    input: CatalogoInput,
    motivo: string,
  ): Observable<CatalogoItem> {
    return this.http
      .put<unknown>(
        `${this.base}/${encodeURIComponent(clave)}/${id}`,
        input,
        this.conMotivo(motivo),
      )
      .pipe(unwrapApiResponse<CatalogoItem>());
  }

  desactivar(clave: string, id: number, motivo: string): Observable<CatalogoItem> {
    return this.http
      .patch<unknown>(
        `${this.base}/${encodeURIComponent(clave)}/${id}/desactivar`,
        {},
        this.conMotivo(motivo),
      )
      .pipe(unwrapApiResponse<CatalogoItem>());
  }

  reactivar(clave: string, id: number, motivo: string): Observable<CatalogoItem> {
    return this.http
      .patch<unknown>(
        `${this.base}/${encodeURIComponent(clave)}/${id}/activar`,
        {},
        this.conMotivo(motivo),
      )
      .pipe(unwrapApiResponse<CatalogoItem>());
  }
}

function aParams(f: CatalogoFiltros): HttpParams {
  let p = new HttpParams();
  if (f.busqueda?.trim()) p = p.set('busqueda', f.busqueda.trim());
  if (f.activo !== undefined) p = p.set('activo', f.activo);
  if (f.page !== undefined) p = p.set('page', f.page);
  if (f.pageSize !== undefined) p = p.set('pageSize', f.pageSize);
  if (f.orderBy !== undefined) p = p.set('orderBy', f.orderBy);
  if (f.orderDir !== undefined) p = p.set('orderDir', f.orderDir);
  return p;
}
